import { DEFAULT_ENABLE_WATERMARK, EVENT_NAMES, type LayoutType } from '@/constants';
import type {
  ActionData,
  BackgroundContent,
  BookData,
  BookPageData,
  ElementData,
  FontInfo,
  ProjectData
} from '@/types';
import { emitter } from '@/utils/emitter';
import { updateBookPageIndex } from '@/utils/canvasUtil';
import { restoreRelativePaths, buildReplaceRecord } from '@/utils/projectUtil';

import { nanoid } from 'nanoid';
import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';

export const useProjectStore = defineStore('project', () => {
  const isLoading = ref(false);
  const isModify = ref(false);
  const isCoverModify = ref(false);

  const id = ref<string | number>('');
  const name = ref('');
  const version = ref('');
  const layout = ref<LayoutType | ''>('');
  const background = ref<BackgroundContent | null>(null);
  const bgm = ref<ActionData | null>(null);

  /** 用户设定的音量档位（0~1），与 muted 独立存储。 */
  const rawVolume = ref(1);
  const muted = ref(false);

  const book = ref<BookData>({ id: `book-${nanoid(6)}`, pages: [] });

  // 当前选中页 id
  const currentPageId = ref('');

  const currentActionId = ref('');

  const fileOptions = ref<Map<string, { savePath: string; removePath: string }>>(new Map());

  const baseUrl = ref<string>('');
  const shareUrl = ref<string>('');
  const shareCoverUrl = ref<string>('');
  const isFree = ref<boolean>(false);
  const enable_watermark = ref<boolean>(true);

  /** 上次保存/加载时的字体→文本快照，用于判断保存时是否需要重新子集化 */
  const fontTextMap = ref<Map<string, string>>(new Map());

  /** 上次 processFontSubset 的结果缓存 */
  const lastFonts = ref<FontInfo[]>([]);

  const pages = computed({
    get() {
      return book.value?.pages ?? [];
    },
    set(value) {
      if (!book.value) return;
      book.value.pages = value;
    }
  });

  const currentPage = computed(() => {
    return pages.value.find((p) => p.id === currentPageId.value) ?? null;
  });

  const currentAction = computed(() => {
    return currentPage.value?.actions?.find((e) => e.id === currentActionId.value) ?? null;
  });

  /**
   * 全局音量（0~1）：写入时自动钳制；设为 0 时同步静音。
   */
  const volume = computed({
    get: () => rawVolume.value,
    set: (value: number) => {
      rawVolume.value = Math.max(0, Math.min(1, value));
      muted.value = rawVolume.value === 0;
    }
  });

  /** 切换静音：取消静音时若音量为 0，回弹到满音量。 */
  const toggleMuted = () => {
    muted.value = !muted.value;
    if (!muted.value && volume.value === 0) volume.value = 1;
  };

  const reset = () => {
    isLoading.value = false;
    isModify.value = false;
    isCoverModify.value = false;
    id.value = '';
    name.value = '';
    version.value = '';
    layout.value = '';
    background.value = null;
    bgm.value = null;
    book.value = { id: `book-${nanoid(6)}`, pages: [] };
    isFree.value = true;
    enable_watermark.value = true;
    baseUrl.value = '';
    shareUrl.value = '';
    shareCoverUrl.value = '';
    currentPageId.value = '';
    fileOptions.value = new Map();
    fontTextMap.value = new Map();
    lastFonts.value = [];
  };

  /**
   * 加载工程到 store。
   * @param options.skipCanvasLoad 为 true 时只 hydrate 数据，不触发 PROJECT_LOAD
   * （供 Create/Story 预览弹窗使用，画布由 PROJECT_PREVIEW_LOAD 接管）
   */
  const load = async (data: ProjectData, options?: { skipCanvasLoad?: boolean }) => {
    id.value = data.id ?? '';
    name.value = data.name ?? '';
    version.value = data.version ?? '';
    layout.value = data.layout ?? '';
    background.value = data.background ?? null;
    bgm.value = data.bgm ?? null;
    book.value = data.book ?? { id: `book-${nanoid(6)}`, pages: [] };
    baseUrl.value = data.baseUrl ?? '';
    shareUrl.value = data.shareUrl ?? '';
    shareCoverUrl.value = data.shareCoverUrl ?? '';
    enable_watermark.value = data.enable_watermark ?? DEFAULT_ENABLE_WATERMARK;

    if (pages.value.length > 0) {
      currentPageId.value = pages.value[0].id;
    } else {
      currentPageId.value = '';
    }

    // 预览兜底：无编辑画布监听 PROJECT_LOAD，跳过以免 Promise 挂起
    if (options?.skipCanvasLoad) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    await new Promise((resolve, reject) => {
      emitter.emit(EVENT_NAMES.PROJECT_LOAD, { resolve, reject });
    });

    isLoading.value = false;
  };

  const updateProjectName = (title: string) => {
    isModify.value = true;
    name.value = title;
  };

  const updateCoverModify = (value: boolean) => {
    if (
      value &&
      currentPageId.value &&
      pages.value.length > 0 &&
      currentPageId.value === pages.value[0].id
    ) {
      isCoverModify.value = value;
    } else {
      isCoverModify.value = false;
    }
  };

  const addPage = (...pageDatas: BookPageData[]) => {
    if (pageDatas.length === 0) return;

    pages.value.push(...pageDatas);

    // 新增的页直接作为当前页显示
    currentPageId.value = pageDatas[0].id;

    isModify.value = true;

    emitter.emit(EVENT_NAMES.BOOK_PAGE_ADD, { pages: pageDatas });
  };

  const removePage = (...ids: string[]) => {
    const removedPages = pages.value.filter((p) => ids.includes(p.id));
    const newPages = pages.value.filter((p) => ids.includes(p.id) === false);

    pages.value = updateBookPageIndex(newPages);

    // 清理被删除页面的文件替换记录，避免保存时仍上传/删除已删除页面的文件
    removedPages.forEach((page) => {
      (page.actions ?? []).forEach((action) => {
        fileOptions.value.delete(action.id);
      });
    });

    // 删除的是当前页时，回退到剩余的第一页（没有则置空），并清空当前元素选中
    if (ids.includes(currentPageId.value)) {
      currentPageId.value = pages.value[0]?.id ?? '';
      currentActionId.value = '';
      isCoverModify.value = true;
    }

    isModify.value = true;

    emitter.emit(EVENT_NAMES.BOOK_PAGE_REMOVE, { ids });
  };

  const setCurrentPage = (id: string) => {
    currentPageId.value = id;
  };

  const addPageAction = (actionData: ActionData) => {
    isModify.value = true;
    updateCoverModify(true);
    currentPage.value?.actions?.push(actionData);
  };

  const removePageAction = (id: string) => {
    if (!currentPage.value) return;
    isModify.value = true;
    updateCoverModify(true);
    currentPage.value.actions = currentPage.value?.actions?.filter((e) => e.id !== id);
  };

  const addBgm = (actionData: ActionData) => {
    isModify.value = true;
    bgm.value = actionData;
  };

  const removeBgm = () => {
    isModify.value = true;
    bgm.value = null;
  };

  const setCurrentAction = (id: string) => {
    currentActionId.value = id;
  };

  // 同步元素数据到 store（transform / drag 结束后调用）
  const updateElementData = (elementId: string, elementData: ElementData) => {
    let action = currentAction.value;

    if (!action || action.element?.id !== elementId) {
      action = currentPage.value?.actions?.find((a) => a.element?.id === elementId) ?? null;
    }

    if (action) {
      // 创建新对象引用，确保 Vue 响应式系统检测到变化
      action.element = { ...elementData };
    }

    isModify.value = true;
    updateCoverModify(true);
  };

  const updateFileOption = (id: string, savePath: string, removePath: string) => {
    if (fileOptions.value.has(id)) {
      const option = fileOptions.value.get(id);
      if (option) {
        option.savePath = savePath;
        option.removePath = removePath;
      }
    } else {
      fileOptions.value.set(id, { savePath, removePath });
    }
  };

  const getSaveData = () => {
    const data: ProjectData = JSON.parse(
      JSON.stringify({
        id: id.value,
        name: name.value,
        version: version.value,
        layout: layout.value,
        baseUrl: baseUrl.value,
        shareUrl: shareUrl.value,
        shareCoverUrl: shareCoverUrl.value,
        book: toRaw(book.value),
        isFree: isFree.value,
        ...(bgm.value ? { bgm: toRaw(bgm.value) } : {}),
        ...(background.value ? { background: toRaw(background.value) } : {})
      })
    );

    // 将 book 所有页的 actions 与 bgm 的 action 平铺为一个数组
    const allActions = [
      ...(data.book?.pages?.flatMap((p) => p.actions ?? []) ?? []),
      ...(data.bgm ? [data.bgm] : [])
    ];

    // 从 fileOptions 构建文件替换记录
    const files = buildReplaceRecord(fileOptions.value);

    // 统一处理路径：baseUrl 元素还原相对路径，其余改为 resources/extfile/
    restoreRelativePaths(allActions, baseUrl.value);

    return { data, files };
  };

  /**
   * 存储字体快照（文本映射 + 子集化结果），加载和保存成功后调用
   */
  const setFontSnapshot = (map: Map<string, string>, fonts: FontInfo[]) => {
    fontTextMap.value = new Map(map);
    lastFonts.value = fonts;
  };

  /**
   * 获取上次缓存的字体子集化结果
   */
  const getLastFonts = (): FontInfo[] => {
    return lastFonts.value;
  };

  return {
    isLoading,
    isModify,
    isCoverModify,
    id,
    name,
    version,
    layout,
    background,
    bgm,
    volume,
    muted,
    book,
    pages,
    currentPageId,
    currentPage,
    currentActionId,
    currentAction,
    isFree,
    enable_watermark,
    baseUrl,
    shareUrl,
    shareCoverUrl,
    fontTextMap,
    load,
    reset,
    toggleMuted,
    updateProjectName,
    updateCoverModify,
    addPage,
    removePage,
    addBgm,
    removeBgm,
    setCurrentPage,
    addPageAction,
    removePageAction,
    setCurrentAction,
    updateElementData,
    updateFileOption,
    getSaveData,
    setFontSnapshot,
    getLastFonts
  };
});
